import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { isCvinExpansion, formatExpansionWorkOrderForDisplay } from "../projectGridColumns";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { isCommercialProjectRow } from "./commercialProjectAcf";
import {
  ConstructionScheduleForm,
  FiberScheduleForm,
  NetworkingInstallationsForm,
} from "./PhaseSchedulePrompt";
import {
  domainsFromProjectRow,
  mergeDomainFields,
  patchCommercialDomains,
  readConstructionSchedule,
  readFiberSchedule,
  readNetworkingInstallations,
} from "./commercialScheduleApi";
import { notifyNetworkingInstallationEmails } from "./networkingInstallationApi";
import { formatScheduleDateOnly } from "./constructionScheduleDates";

/** Networking is commercial-only; construction / splicing also include expansion WOs. */
function projectsForScheduleEventType(projects, eventType) {
  if (eventType === "networking") {
    return projects.filter((row) => isCommercialProjectRow(row));
  }
  return projects.filter((row) => isCommercialProjectRow(row) || isCvinExpansion(row));
}

function scheduleProjectLabel(row) {
  if (isCvinExpansion(row)) {
    return formatExpansionWorkOrderForDisplay(row) || row.work_order || `#${row.id}`;
  }
  return row.work_order || `#${row.id}`;
}

const EVENT_TYPE_CHANGE_LOG_DOMAINS = {
  construction: [COMMERCIAL_DOMAINS.CONSTRUCTION],
  splicing: [COMMERCIAL_DOMAINS.FIBER],
  networking: [COMMERCIAL_DOMAINS.NETWORKING],
};

function emptyNetworkingRow(startDate = "") {
  return {
    location: "",
    technician: "",
    start_date: startDate,
    end_date: "",
    equipment_notes: "",
    voip_installation: false,
    complete: false,
    schedule_email_sent: false,
  };
}

function seedDomainsForDay(domains, eventType, dayStr) {
  if (eventType === "construction") {
    return mergeDomainFields(domains, COMMERCIAL_DOMAINS.CONSTRUCTION, {
      ...readConstructionSchedule(domains),
      construction_start_date: dayStr,
    });
  }
  if (eventType === "splicing") {
    return mergeDomainFields(domains, COMMERCIAL_DOMAINS.FIBER, {
      ...readFiberSchedule(domains),
      splicing_start_date: dayStr,
    });
  }
  if (eventType === "networking") {
    const installations = readNetworkingInstallations(domains);
    return mergeDomainFields(domains, COMMERCIAL_DOMAINS.NETWORKING, {
      installations: [...installations, emptyNetworkingRow(dayStr)],
    });
  }
  return domains;
}

function ScheduleProjectPicker({ projects, selectedId, onSelect, disabled }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...projects].sort((a, b) =>
      String(scheduleProjectLabel(a)).localeCompare(String(scheduleProjectLabel(b)), undefined, {
        numeric: true,
      })
    );
    if (!q) return list.slice(0, 100);
    return list
      .filter((row) => {
        const hay = `${scheduleProjectLabel(row)} ${row.name || ""} ${row.customer || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 100);
  }, [projects, query]);

  return (
    <div className="schedule-calendar-day-project-picker">
      <label className="phase-schedule-field">
        <span className="phase-schedule-field-label">Project</span>
        <input
          type="search"
          className="commercial-field-input"
          placeholder="Search work order, name, customer…"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <div className="schedule-calendar-day-project-list" role="listbox" aria-label="Projects">
        {filtered.length === 0 ? (
          <p className="project-detail-muted">No projects match.</p>
        ) : (
          filtered.map((row) => {
            const selected = row.id === selectedId;
            return (
              <button
                key={row.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={`schedule-calendar-day-project-row${selected ? " is-selected" : ""}`}
                disabled={disabled}
                onClick={() => onSelect(row)}
              >
                <strong>{scheduleProjectLabel(row)}</strong>
                <span className="schedule-calendar-day-project-row-meta">
                  {[row.name, row.customer].filter(Boolean).join(" · ")}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Opens when a manager double-clicks a calendar day to add or update a schedule.
 */
export default function ScheduleCalendarDayModal({
  day,
  projects,
  editableEventTypes,
  onClose,
  onSaved,
  apiUrl,
  getAuthHeaders,
}) {
  const dayStr = formatScheduleDateOnly(day);
  const dayLabel = day.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const [eventType, setEventType] = useState(() => editableEventTypes[0]?.id || "construction");
  const [projectRow, setProjectRow] = useState(null);
  const [domains, setDomains] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectableProjects = useMemo(
    () => projectsForScheduleEventType(projects, eventType),
    [projects, eventType]
  );

  useEffect(() => {
    if (!editableEventTypes.some((t) => t.id === eventType)) {
      setEventType(editableEventTypes[0]?.id || "construction");
    }
  }, [editableEventTypes, eventType]);

  useEffect(() => {
    if (!projectRow) return;
    const stillSelectable = selectableProjects.some((row) => row.id === projectRow.id);
    if (!stillSelectable) {
      setProjectRow(null);
      setDomains({});
    }
  }, [selectableProjects, projectRow]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const selectProject = (row) => {
    setProjectRow(row);
    const base = domainsFromProjectRow(row, row.acf_domains);
    setDomains(seedDomainsForDay(base, eventType, dayStr));
    setError("");
  };

  const changeEventType = (nextType) => {
    setEventType(nextType);
    const nextProjects = projectsForScheduleEventType(projects, nextType);
    const stillSelectable =
      projectRow != null && nextProjects.some((row) => row.id === projectRow.id);
    if (projectRow && stillSelectable) {
      const base = domainsFromProjectRow(projectRow, projectRow.acf_domains);
      setDomains(seedDomainsForDay(base, nextType, dayStr));
    } else if (projectRow && !stillSelectable) {
      setProjectRow(null);
      setDomains({});
    }
    setError("");
  };

  const patchConstruction = (patch) => {
    setDomains((prev) => mergeDomainFields(prev, COMMERCIAL_DOMAINS.CONSTRUCTION, patch));
  };

  const patchFiber = (patch) => {
    setDomains((prev) => mergeDomainFields(prev, COMMERCIAL_DOMAINS.FIBER, patch));
  };

  const patchNetworkingInstallations = (installations) => {
    setDomains((prev) =>
      mergeDomainFields(prev, COMMERCIAL_DOMAINS.NETWORKING, { installations })
    );
  };

  const save = async () => {
    if (!projectRow) {
      setError("Select a project first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await patchCommercialDomains({
        projectId: projectRow.id,
        projectRow,
        domains,
        getAuthHeaders,
        apiUrl,
        source: "schedule_calendar",
        userEmail: getViewerEmailFromStorage(),
        changeLogDomains: EVENT_TYPE_CHANGE_LOG_DOMAINS[eventType] || null,
      });
      if (eventType === "networking") {
        await notifyNetworkingInstallationEmails({
          projectId: projectRow.id,
          getAuthHeaders,
          apiUrl,
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not save schedule.");
    } finally {
      setSaving(false);
    }
  };

  if (!day || editableEventTypes.length === 0) return null;

  return (
    <div
      className="schedule-calendar-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="schedule-calendar-modal schedule-calendar-modal--day-create"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-calendar-day-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="schedule-calendar-day-modal-title" className="schedule-calendar-modal-title">
          Schedule for {dayLabel}
        </h2>
        <p className="schedule-calendar-modal-meta">
          Choose a schedule type and project, then enter the details below.
        </p>

        <div className="schedule-calendar-day-type-tabs" role="tablist" aria-label="Schedule type">
          {editableEventTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              role="tab"
              aria-selected={eventType === type.id}
              className={`schedule-calendar-day-type-tab${eventType === type.id ? " is-active" : ""}`}
              disabled={saving}
              onClick={() => changeEventType(type.id)}
            >
              {type.label}
            </button>
          ))}
        </div>

        <ScheduleProjectPicker
          projects={selectableProjects}
          selectedId={projectRow?.id ?? null}
          onSelect={selectProject}
          disabled={saving}
        />

        {projectRow ? (
          <div className="phase-schedule-prompt-forms schedule-calendar-modal-forms">
            {eventType === "construction" ? (
              <ConstructionScheduleForm
                schedule={readConstructionSchedule(domains)}
                onPatch={patchConstruction}
                disabled={saving}
              />
            ) : null}
            {eventType === "splicing" ? (
              <FiberScheduleForm
                schedule={readFiberSchedule(domains)}
                onPatch={patchFiber}
                disabled={saving}
              />
            ) : null}
            {eventType === "networking" ? (
              <NetworkingInstallationsForm
                installations={readNetworkingInstallations(domains)}
                onChange={patchNetworkingInstallations}
                disabled={saving}
              />
            ) : null}
          </div>
        ) : (
          <p className="project-detail-muted schedule-calendar-day-pick-project-hint">
            Select a project to enter schedule details.
          </p>
        )}

        {error ? (
          <p className="project-detail-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="schedule-calendar-modal-actions">
          <button
            type="button"
            className="project-landing-edit-btn"
            disabled={saving || !projectRow}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save schedule"}
          </button>
          {projectRow ? (
            <Link
              to={`/project/${projectRow.id}`}
              className="phase-schedule-project-link"
              onClick={onClose}
            >
              Open project
            </Link>
          ) : null}
          <button type="button" className="email-triggers-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
